import { AccessCodeBaseEnum } from '../../user-access/models/access-code.enum';
import { hasAccessToRule } from '../../../shared/shared-utils/user-access/user-access.utils';
import { SharedModalUtils } from '../../../shared/shared-utils/shared-modal/shared-modal.utils';
import { IconsEnum } from '../../../shared-modules/shared-icons/models/icons.enum';
import { IconColourEnum } from '../../../shared-modules/shared-icons/models/icons-colour.enum';

export type StockTakeManagerRestrictedAction = 'finalise' | 'delete';

/**
 * Whether the user has stock-take manager access for a store access code.
 */
export function hasStockTakeManagerAccessForStore(storeAccessCode: number): boolean {
  return hasAccessToRule(storeAccessCode, AccessCodeBaseEnum.StockTakeManagerAccess);
}

/**
 * Whether the user has stock-take count access for a store access code.
 */
export function hasStockTakeCountAccessForStore(storeAccessCode: number): boolean {
  return hasAccessToRule(storeAccessCode, AccessCodeBaseEnum.StockTakeCountAccess);
}

/**
 * Whether the user can access the stock take module for a store (count or manager).
 */
export function hasStockTakeAccessForStore(storeAccessCode: number): boolean {
  return hasStockTakeCountAccessForStore(storeAccessCode) || hasStockTakeManagerAccessForStore(storeAccessCode);
}

/**
 * Whether the counting detail view must be used instead of the manager detail view.
 *
 * The manager detail route requires stock take manager access, so count-only users are kept on the
 * counting workflow regardless of viewport.
 */
export function shouldUseStockTakeCountingDetail(storeAccessCode: number, isMobile: boolean): boolean {
  return isMobile || !hasStockTakeManagerAccessForStore(storeAccessCode);
}

/**
 * Resolves the stock take detail route the user is permitted to open.
 */
export function getStockTakeDetailRoute(storeAccessCode: number, isMobile: boolean, stockTakeId: string): (string)[] {
  return shouldUseStockTakeCountingDetail(storeAccessCode, isMobile)
    ? ['/stock-take/mobile-detail', stockTakeId]
    : ['/stock-take/detail', stockTakeId];
}

/**
 * Prompts count-only users when they attempt a manager-only stock take action.
 *
 * @returns `true` when the user may proceed; `false` after showing the access modal.
 */
export async function ensureStockTakeManagerAccess(
  sharedModalUtils: SharedModalUtils,
  storeAccessCode: number,
  action: StockTakeManagerRestrictedAction,
): Promise<boolean> {
  if (hasStockTakeManagerAccessForStore(storeAccessCode)) {
    return true;
  }

  await showStockTakeManagerAccessDenied(sharedModalUtils, action);
  return false;
}

/**
 * Shows the insufficient-access modal for a manager-only stock take action.
 */
export async function showStockTakeManagerAccessDenied(
  sharedModalUtils: SharedModalUtils,
  action: StockTakeManagerRestrictedAction,
): Promise<void> {
  const actionLabel = action === 'finalise' ? 'finalise stock takes' : 'delete stock takes';
  await sharedModalUtils.createModal({
    modalTitleIcon: IconsEnum.infoSlabCircleOutline,
    modalTitleIconColor: IconColourEnum.red,
    modalTitle: 'Insufficient Access',
    contextDescription: [
      `You do not have permission to ${actionLabel}.`,
      'Stock take manager access is required for this action.',
    ],
    buttonAccept: false,
    buttonAcceptText: '',
    buttonClose: true,
    buttonCloseText: 'OK',
    buttonCloseCross: false,
    cssClass: ['shared-basic-modal-css-small'],
  });
}
